// @ts-check
/**
 * Typed client for the admin user management API.
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 *
 *     npm run generate:types-from-openapi-schema
 */
import axios from 'axios'

/** @typedef {import('./schema').components['schemas']} Schemas */

/** @typedef {Schemas['AdminAppUser']} AdminAppUser */
/** @typedef {Schemas['AdminAppUserList']} AdminAppUserList */
/** @typedef {Schemas['CreateUserRequest']} CreateUserRequest */
/** @typedef {Schemas['CreateUserResponse']} CreateUserResponse */
/** @typedef {Pick<Schemas['PatchedAdminAppUser'], 'first_name' | 'last_name' | 'is_active'>} UpdateUserRequest */

/**
 * GET /api/admin/user/ — list all users for the current tenant.
 * @returns {Promise<AdminAppUserList[]>}
 */
export async function listUsers() {
  const { data } = await axios.get('/api/admin/user/')
  return data
}

/**
 * GET /api/admin/user/:id/ — get a single user.
 * @param {number} userId
 * @returns {Promise<AdminAppUser>}
 */
export async function getUser(userId) {
  const { data } = await axios.get(`/api/admin/user/${userId}/`)
  return data
}

/**
 * POST /api/admin/user/create/ — create a new user.
 * @param {CreateUserRequest} payload
 * @returns {Promise<CreateUserResponse>}
 */
export async function createUser(payload) {
  const { data } = await axios.post('/api/admin/user/create/', payload)
  return data
}

/**
 * PATCH /api/admin/user/:id/ — partially update a user.
 * @param {number} userId
 * @param {UpdateUserRequest} fields
 * @returns {Promise<AdminAppUser>}
 */
export async function updateUser(userId, fields) {
  const { data } = await axios.patch(`/api/admin/user/${userId}/`, fields)
  return data
}

/**
 * POST /api/appuseraccessroles/batch_update/ — replace a user's access roles.
 * @param {number} userId
 * @param {string[]} roles
 * @returns {Promise<void>}
 */
export async function batchUpdateAccessRoles(userId, roles) {
  await axios.post('/api/appuseraccessroles/batch_update/', {
    user_id: userId,
    roles,
  })
}

/**
 * POST /api/appuserportalroles/batch_update/ — replace a user's portal roles.
 * @param {number} userId
 * @param {number[]} roleIds
 * @returns {Promise<void>}
 */
export async function batchUpdatePortalRoles(userId, roleIds) {
  await axios.post('/api/appuserportalroles/batch_update/', {
    user_id: userId,
    role_ids: roleIds,
  })
}

/**
 * POST /api/appuserentities/batch_update/ — replace a user's entity assignments.
 * @param {number} userId
 * @param {number[]} entityIds
 * @returns {Promise<void>}
 */
export async function batchUpdateEntities(userId, entityIds) {
  await axios.post('/api/appuserentities/batch_update/', {
    user_id: userId,
    entity_ids: entityIds,
  })
}
